import { useEffect, useState, useRef } from 'react';
import {
  inputClassName,
  labelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from '../../components/ui';

export default function RenameBatchModal({
  isOpen,
  onClose,
  onConfirm,
  currentName,
  submitting,
}) {
  const [name, setName] = useState(currentName || '');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName || '');
      setError('');
      // Small timeout to focus input
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
    }
  }, [isOpen, currentName]);

  useEffect(() => {
    if (!isOpen || submitting) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Batch name cannot be empty.');
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/60 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="crm-panel w-full max-w-md overflow-hidden shadow-2xl"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg font-semibold text-[rgb(var(--crm-text))]">
            Rename Batch
          </h2>
          <p className="crm-text-muted mt-1.5 text-sm">
            Enter a new name for this procurement batch.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-2">
            <label className="grid gap-1.5">
              <span className={labelClassName}>Batch Name</span>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) {
                    setError('');
                  }
                }}
                className={inputClassName}
                placeholder="Enter batch name…"
                disabled={submitting}
                required
              />
            </label>
            {error && (
              <p className="mt-2 text-xs text-red-600 font-medium">
                {error}
              </p>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-6 py-4">
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={primaryButtonClassName}
              disabled={submitting || !name.trim() || name.trim() === currentName}
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
